import {Injectable, OnDestroy} from '@angular/core';
import {ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router} from "@angular/router";
import {Breadcrumb} from "../../shared/components/breadcrumbs/models/breadcrumb.model";
import {TranslateService} from "@ngx-translate/core";
import {BehaviorSubject, combineLatestWith, filter, startWith, Subject, takeUntil} from "rxjs";

@Injectable()
export class BreadcrumbsService implements OnDestroy {
  private readonly breadcrumbsSubject$ = new BehaviorSubject<Breadcrumb[]>([]);

  public readonly breadcrumbs$ = this.breadcrumbsSubject$.asObservable();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    this.route.data
      .pipe(
        combineLatestWith(this.translateService.onLangChange.pipe(
          startWith(this.translateService.currentLang)
        )),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
      this.initBreadcrumbs();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initBreadcrumbs(): void {
    this.breadcrumbsSubject$.next(this.buildBreadcrumbs(this.router.routerState.snapshot.root));

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.breadcrumbsSubject$.next(this.buildBreadcrumbs(this.router.routerState.snapshot.root));
    });
  }

  private buildBreadcrumbs(
    route: ActivatedRouteSnapshot,
    url: string = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const routeSegment = route.url.map(segment => segment.path).join('/');

    if (routeSegment) {
      url += `/${routeSegment}`;
      const breadcrumbData = route.data['breadcrumb'];

      if (breadcrumbData) {
        const label = typeof breadcrumbData === 'function'
          ? breadcrumbData(route, this.translateService)
          : this.translateService.instant(breadcrumbData || this.formatLabel(routeSegment));

        breadcrumbs.push({
          label,
          path: () => ({ url }),
          iconPath: route.data['iconPath']
        });
      }
    }

    if (route.firstChild) {
      return this.buildBreadcrumbs(route.firstChild, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private formatLabel(segment: string): string {
    return segment.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }
}
